import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from "react";
import { createPortal } from "react-dom";

/**
 * v1.22.9 · DYA-005 — Diyafa-branded toast system.
 *
 * Usage:
 *   const { toast } = useToast();
 *   toast.success("Reservation created");
 *   toast.rasadSynced("Guest synced to Rasad");
 *
 * No runtime deps. Portal-rendered in the top-right corner.
 */

type Variant = "success" | "error" | "info" | "rasad";

interface ToastItem {
  id: number;
  variant: Variant;
  title: string;
  description?: string;
}

interface ToastApi {
  success: (title: string, description?: string) => void;
  error:   (title: string, description?: string) => void;
  info:    (title: string, description?: string) => void;
  /** Special variant with shield icon — used for Rasad events. */
  rasadSynced: (title: string, description?: string) => void;
}

const ToastCtx = createContext<{ toast: ToastApi } | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);

  const push = useCallback((variant: Variant, title: string, description?: string) => {
    const id = Date.now() + Math.random();
    setItems((prev) => [...prev, { id, variant, title, description }]);
    setTimeout(() => {
      setItems((prev) => prev.filter((t) => t.id !== id));
    }, 5000);
  }, []);

  const api: ToastApi = {
    success: (t, d) => push("success", t, d),
    error:   (t, d) => push("error", t, d),
    info:    (t, d) => push("info", t, d),
    rasadSynced: (t, d) => push("rasad", t, d),
  };

  return (
    <ToastCtx.Provider value={{ toast: api }}>
      {children}
      <RasadSyncBridge />
      <ToastViewport items={items} onDismiss={(id) => setItems((p) => p.filter((t) => t.id !== id))} />
    </ToastCtx.Provider>
  );
}

/**
 * v1.22.9 · DYA-005 — Listen for CustomEvents dispatched by the Rasad
 * sync layer and surface them as toasts. Runs once, globally, inside
 * the provider tree so every component benefits without wiring.
 */
function RasadSyncBridge() {
  const { toast } = useToast();
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as { ok: boolean; kind: string; eventType: string; error?: string };
      if (!detail) return;
      const label = `${detail.kind === "rental" ? "Rental" : "Reservation"} · ${detail.eventType}`;
      if (detail.ok) {
        toast.rasadSynced("Rasad sync successful", label);
      } else {
        toast.error("Rasad sync failed", detail.error ?? label);
      }
    };
    window.addEventListener("diyafa:rasad-sync", handler as EventListener);
    return () => window.removeEventListener("diyafa:rasad-sync", handler as EventListener);
  }, [toast]);
  return null;
}

export function useToast(): { toast: ToastApi } {
  const ctx = useContext(ToastCtx);
  if (!ctx) {
    // Gracefully degrade when provider not mounted (e.g. during SSR /
    // Jest) — log to console instead of throwing.
    return {
      toast: {
        success: (t, d) => console.log("[toast success]", t, d),
        error:   (t, d) => console.warn("[toast error]", t, d),
        info:    (t, d) => console.log("[toast info]", t, d),
        rasadSynced: (t, d) => console.log("[toast rasad]", t, d),
      },
    };
  }
  return ctx;
}

function ToastViewport({ items, onDismiss }: { items: ToastItem[]; onDismiss: (id: number) => void }) {
  if (typeof document === "undefined") return null;
  return createPortal(
    <div className="fixed top-4 end-4 z-[80] flex flex-col gap-2 max-w-sm pointer-events-none">
      {items.map((t) => (
        <ToastCard key={t.id} item={t} onDismiss={() => onDismiss(t.id)} />
      ))}
    </div>,
    document.body,
  );
}

function ToastCard({ item, onDismiss }: { item: ToastItem; onDismiss: () => void }) {
  const [visible, setVisible] = useState(false);
  useEffect(() => { setVisible(true); }, []);

  const meta = {
    success: { icon: "ri-check-line",         cls: "border-green-200 bg-white",     iconCls: "bg-green-500 text-white" },
    error:   { icon: "ri-error-warning-line", cls: "border-red-200 bg-white",       iconCls: "bg-red-500 text-white" },
    info:    { icon: "ri-information-line",   cls: "border-blue-200 bg-white",      iconCls: "bg-blue-500 text-white" },
    rasad:   { icon: "ri-shield-check-line",  cls: "border-line-strong bg-gradient-to-r from-gold-soft to-white", iconCls: "bg-gold text-ink" },
  }[item.variant];

  return (
    <div
      className={`
        pointer-events-auto rounded-xl border shadow-lg px-3 py-2.5 flex items-start gap-2.5 min-w-[280px]
        transition-all duration-200
        ${meta.cls}
        ${visible ? "translate-x-0 opacity-100" : "translate-x-4 opacity-0"}
      `}
      role="status"
    >
      <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${meta.iconCls}`}>
        <i className={`${meta.icon} text-sm`}></i>
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-ink">{item.title}</p>
        {item.description && <p className="text-[11px] text-muted mt-0.5">{item.description}</p>}
      </div>
      <button onClick={onDismiss} aria-label="Dismiss"
        className="w-6 h-6 flex items-center justify-center rounded-md text-muted hover:text-ink hover:bg-cream cursor-pointer flex-shrink-0">
        <i className="ri-close-line text-sm"></i>
      </button>
    </div>
  );
}
